

import { Component, Vue } from 'vue-property-decorator';
import { showServerError } from '@/utils';
import { Getter, Mutation, namespace } from 'vuex-class';
import { PAGINATION_PER_PAGE } from '@/configs/global';
import {
    getOffersList,
    getAffiliatesList,
    editErrorType,
    addErrorType,
    deleteErrorType, downloadErrorOfferRuleFile, downloadErrorsRule,
} from '@/api/uapi';
import { getCurrentDate } from '@/utils/formatDates';
import { getStatsErrorUnsatisfied } from '@/api/uapi';
import SvgApplication from '@/assets/icons/nav-bar/application.svg';
import AppTable from '@/components/base/table/AppTable.vue';
import CardMaterial from '@/components/base/CardMaterial.vue';
import FormModal from '@/components/base/FormModal.vue';
import { IFilter } from '@/types';
import { IGroupingStatsError, IStatsError } from '@/api/types/uapi';
import TooltipButton from '@/components/base/buttons/TooltipButton.vue';
import { eventBus } from '@/eventbus';
import Filters from '@/components/base/filters/Filters.vue';
import { omit } from 'lodash-es';
import { filterClass } from '@/services/filters/filterClass';

const auth = namespace('authModule');

@Component({
    components: {
        AppTable,
        SvgApplication,
        CardMaterial,
        FormModal,
        TooltipButton,
        Filters,
    },
})
export default class Approvals extends Vue {
    @Mutation('SET_TOTAL_ELEMENTS_DATA_TABLE') setTotalElementsDataTable;
    @Mutation('SET_DEFAULT_FILTERS_PARAMS_OBJECT') setDefaultFiltersParams;
    @Mutation('SET_FILTERS_PARAMS_OBJECT') setFiltersParamsObject;
    @Getter('GET_DEFAULT_FILTERS_PARAMS_OBJECT') getDefaultFiltersParams;
    @Getter('GET_FILTERS_PARAMS_OBJECT') getFiltersParamsObject;
    @auth.Getter('GET_USER') user;

    dataTable: IStatsError[] = [];
    currentDate = getCurrentDate('yyyy-MM-dd');
    filters: IFilter[] = [
        new filterClass.Autocomplete({
            id: 'affiliate_id',
            label: 'ID партнера',
            apiMethod: getAffiliatesList,
            itemValue: 'id',
            itemText: 'name',
            type: 'number',
            select: [],
            items: [],
            multiple: true,
        }),
        new filterClass.Autocomplete({
            id: 'offer_id',
            label: 'Оффер',
            apiMethod: getOffersList,
            itemValue: 'id',
            itemText: 'offer_name',
            type: 'number',
            select: [],
            items: [],
            multiple: true,
        }),
        new filterClass.Datepicker({
            id: 'created',
            label: 'Период',
            pickerType: 'date',
            range: true,
            select: [this.currentDate],
            defaultValue: [this.currentDate],
        }),
    ];
    headers = [
        { text: 'ID оффера', value: 'offer_id' },
        { text: 'Название оффера', value: 'offer_name' },
        { text: 'ID партнёра', value: 'affiliate_id' },
        { text: 'Ошибка', value: 'error' },
        { text: 'Количество', value: 'count' },
        { text: '', value: 'actions' },
    ];
    routes = [
        {
            routeParam: 'id',
            extraRouteParamName: 'offer_id',
            slot: 'item.offer_id',
            routerName: 'show-offer',
            text: 'offer_id',
        },
        {
            routeParam: 'id',
            extraRouteParamName: 'affiliate_id',
            slot: 'item.affiliate_id',
            routerName: 'affiliate',
            text: 'affiliate_id',
        },
    ];
    uniqueData = [
        {
            slot: 'item.count',
        },
    ];
    settings = {
        title: 'Ошибки правил оффера',
        loading: false,
    };
    totalElementDataTable: number = 0;
    paginationItemsPerPage = PAGINATION_PER_PAGE;
    errorId: number | string = '';
    groupingModal = false;
    deletingModal = false;
    showModal = false;
    grouping: IGroupingStatsError = {
        offer_id: null,
        name: '',
        regex: '',
    };

    created(): void {
        this.prepareFilters();
    }

    mounted(): void {
        this.getRulesErrorPagination();
    }

    showDeletingStageModal(item: IStatsError): void {
        this.errorId = item.error_type_id!;
        this.deletingModal = true;
    }

    showGroupingModal(item: IStatsError): void {
        if (item.error_type_id) {
            this.grouping.error_id = item.error_type_id;
            this.grouping.offer_id = item.offer_id;
            this.grouping.regex = item.pattern!;
            this.grouping.name = item.error!;
        } else {
            this.grouping.offer_id = item.offer_id;
            this.grouping.regex = item.error;
        }
        this.groupingModal = true;
    }

    hideGroupingModal(): void {
        for (const key in this.grouping) {
            if (key in this.grouping) {
                this.grouping[key] = '';
            }
        }
        this.groupingModal = false;
    }

    async saveGrouping(): Promise<void> {
        try {
            if (this.grouping.error_id) {
                await editErrorType(this.grouping.error_id, this.grouping, true);
            } else {
                await addErrorType(this.grouping, true);
            }
            this.hideGroupingModal();
            await this.getDataTable(0, this.paginationItemsPerPage);
        } catch (err) {
            showServerError(err);
        }
    }

    async deleteError(): Promise<void> {
        try {
            await deleteErrorType(String(this.errorId), true);
            await this.getDataTable(0, this.paginationItemsPerPage);
            this.deletingModal = false;
        } catch (err) {
            showServerError(err);
        }
    }

    async getRulesErrorPagination(offset?: number): Promise<void> {
        try {
            await this.getDataTable(offset);
        } catch (err) {
            showServerError(err, 'Список ошибок правил офферов не загружен');
        }
        this.settings.loading = false;
    }

    async getDataTable(offset?: number, limit?: number, isChangeLimit?: boolean): Promise<void> {
        if (offset === 0 || isChangeLimit) {
            eventBus.$emit('go-to-first-page');
        }
        if (limit) {
            this.paginationItemsPerPage = limit;
        }
        this.settings.loading = true;
        const paramsObj = { ...this.getDefaultFiltersParams, ...this.getFiltersParamsObject };
        try {
            const { meta, data } = await getStatsErrorUnsatisfied({ offset, limit : this.paginationItemsPerPage, ...paramsObj });
            this.dataTable = data;
            this.totalElementDataTable = meta!.total!;
        } catch (err) {
            showServerError(err, 'Список ошибок не найден');
        }
        this.settings.loading = false;
    }

    async downloadFile(item: IStatsError): Promise<void> {
        try {
            const { error, offer_id } = item;
            const paramsObj = { ...this.getFiltersParamsObject };
            await downloadErrorsRule({ ...paramsObj, error, offer_id: [offer_id] });
            this.showModal = true;
        } catch (err) {
            showServerError(err, 'Не удалось скачать выгрузку');
        }
    }

    prepareFilters(): void {
        const activeFilters: object[] = [];
        this.filters.map((filter) => {
            if (filter.select) {
                if (
                    (filter.array || filter.multiselect)
                    && filter.select.length
                ) {
                    activeFilters.push({
                        [filter.id]: filter.select,
                    });
                } else if (filter.datepicker) {
                    activeFilters.push({
                        [`${filter.id}_from`]: filter.select[0],
                        [`${filter.id}_to`]: filter.select[0],
                    });
                }
            }
        });
        const paramsObj = JSON.parse(JSON.stringify(Object.assign({}, ...activeFilters)));
        this.setDefaultFiltersParams(paramsObj);
    }

    async downloadError(item: IStatsError): Promise<void> {
        try {
            const { error_type_id } = item;
            if (error_type_id) {
                const paramsObj = { ...this.getDefaultFiltersParams, ...this.getFiltersParamsObject, error_type_id };
                await downloadErrorOfferRuleFile({ ...paramsObj });
            }
        } catch (err) {
            showServerError(err, 'Не удалось скачать выгрузку');
        }
        this.showModal = true;
    }

    clearFilters(): void {
        this.setFiltersParamsObject({});
        this.prepareFilters();
        this.$router.push({ query: omit({ ...this.$route.query }, 'created') });
        this.getRulesErrorPagination();
    }
}
